import React, { useMemo } from "react";
import axios from "axios";
import moment from "moment";
import {
  notification,
  Typography,
  Row,
  Space,
  Button,
  Input,
  TimePicker,
  DatePicker,
  Radio,
} from "antd";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { useConsultationSchedulingState } from "./State";
import Helper from "@/helper";
import translator from "@/translator";

const { Text } = Typography;
const t = translator("front_office.views.onboarding.amo_consultation_scheduling.");

const showNotification = (type, config) => {
  const defaultConfig = {
    className: "ant-custom-notification",
  };

  notification[type]({ ...defaultConfig, ...config });
};

const Form = () => {
  const { state, updateState } = useConsultationSchedulingState();
  const { can_call_now, default_values } = state;

  const formMethods = useForm({
    defaultValues: default_values,
  });

  const {
    watch,
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = formMethods;

  const call_now = watch("call_now");

  const lastWorkingMoment = useMemo(
    () =>
      moment(state.working_hours.end, "HH:mm").subtract(state.consultation_length_mins, "minutes"),
    [],
  );

  const lastWorkingMomentStr = useMemo(
    () => lastWorkingMoment.format("HH:mm"),
    [lastWorkingMoment],
  );

  const getDisabledTime = () => {
    const beginTime = moment(state.working_hours.begin, "HH:mm");
    const endTime = lastWorkingMoment;

    const beginHour = beginTime.hour();
    const beginMinute = beginTime.minute();
    const endHour = endTime.hour();
    const endMinute = endTime.minute();

    const range = (start, end) => Array.from({ length: end - start }, (_, i) => start + i);

    return {
      disabledHours: () => [...range(0, beginHour), ...range(endHour + 1, 24)],
      disabledMinutes: (selectedHour) => {
        if (selectedHour === beginHour) {
          return range(0, beginMinute);
        }
        if (selectedHour === endHour) {
          return range(endMinute + 1, 60);
        }
        return [];
      },
    };
  };

  const onSubmit = async (formData, e) => {
    e.preventDefault();

    try {
      const { data } = await axios({
        method: "POST",
        data: formData,
        url: state.submit_url,
        headers: {
          Accept: "application/json",
          "X-CSRF-TOKEN": Helper.getCSRFToken(),
        },
      });
      if (data.success) {
        updateState(data.amo_consultation_scheduling);
      } else {
        Object.entries(data.errors).map(([_fieldName, errorMessages]) => {
          showNotification("error", {
            description: errorMessages.join(", "),
            message: t("form.error_title"),
          });
        });
      }
    } catch (error) {
      if (error.response) {
        showNotification("error", {
          description: error.response.data,
          message: t("form.error_title"),
        });
      } else if (error.request) {
        showNotification("error", {
          description: t("form.network_error"),
          message: t("form.error_title"),
        });
      } else {
        showNotification("error", { description: error.message, message: t("form.error_title") });
      }
    }
  };

  return (
    <FormProvider {...formMethods}>
      <Space direction="vertical" size={32}>
        <img src="/onboarding/consultation_scheduling_2.svg" alt="" />
        <Space direction="vertical" size={8}>
          <Text strong>{t("form.title")}</Text>
          <Text>{t("form.description")}</Text>
        </Space>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            control={control}
            name="name"
            defaultValue={""}
            rules={{ required: true }}
            render={({ field: { onChange, value, name } }) => (
              <Space className="width-100-p" size={8} direction="vertical">
                <Text>{t("form.name_label")}</Text>
                <Input
                  placeholder={t("form.name_placeholder")}
                  className="ant-custom-input"
                  value={value}
                  onChange={onChange}
                  autoComplete="given-name"
                  name={name}
                />
              </Space>
            )}
          />

          {can_call_now && (
            <Space size={16} direction="vertical">
              <Text>{t("form.date_time_label")}</Text>
              <Controller
                control={control}
                name="call_now"
                render={({ field: { onChange, value, name } }) => (
                  <Radio.Group name={name} onChange={onChange} value={value}>
                    <Space size={16} direction="vertical">
                      <Radio value={true}>{t("form.call_now_option")}</Radio>
                      <Radio value={false}>{t("form.schedule_option")}</Radio>
                    </Space>
                  </Radio.Group>
                )}
              />
            </Space>
          )}

          {!call_now && (
            <Space className="width-100-p" size={8} direction="vertical">
              <Space direction="horizontal">
                <Text>{t("form.when_to_call")}</Text>
                {can_call_now && (
                  <Text type="secondary">
                    {t("form.working_hours_short", {
                      begin: state.working_hours.begin,
                      end: lastWorkingMomentStr,
                    })}
                  </Text>
                )}
              </Space>

              {!can_call_now && (
                <Row>
                  <Text type="secondary">
                    {t("form.working_hours", {
                      begin: state.working_hours.begin,
                      end: lastWorkingMomentStr,
                    })}
                  </Text>
                </Row>
              )}
              <Row justify="space-between">
                <Controller
                  control={control}
                  name="date"
                  render={({ field: { onChange, value, name } }) => (
                    <DatePicker
                      allowClear={false}
                      name={name}
                      style={{ minWidth: 139, maxWidth: 139 }}
                      className="ant-custom-input"
                      popupClassName="ant-custom-picker-dropdown"
                      format="YYYY-MM-DD"
                      placeholder={t("form.date_placeholder")}
                      value={moment(value, "YYYY-MM-DD", true)}
                      onChange={(_, dateString) => {
                        onChange(dateString);
                      }}
                      disabledDate={(current) => current.isBefore(moment(), "day")}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="time"
                  render={({ field: { onChange, value, name } }) => (
                    <TimePicker
                      name={name}
                      allowClear={false}
                      format="HH:mm"
                      placeholder={t("form.time_placeholder")}
                      style={{ minWidth: 139, maxWidth: 139 }}
                      className="ant-custom-input"
                      popupClassName="ant-custom-picker-dropdown"
                      clearIcon={false}
                      showNow={false}
                      disabledTime={getDisabledTime}
                      value={moment(value, "HH:mm", true)}
                      onChange={(_, timeString) => {
                        onChange(timeString);
                      }}
                    />
                  )}
                />
              </Row>
            </Space>
          )}

          <Button
            disabled={isSubmitting || !isValid}
            onClick={() => updateState({ step: "form" })}
            className="ant-custom-btn"
            block
            htmlType="submit"
            type="primary"
          >
            {t("form.next_button")}
          </Button>
        </form>
      </Space>
    </FormProvider>
  );
};

export default Form;
